<template>
  <div class="list-analytics">
    <div>
      <highstock class="chart" :options="options" ref="highchart" />
      <el-scrollbar class="status">
        <ul class="list list-event">
          <li v-for="point in legendPoints" :key="point.key" :class="getLegendClass(point.series)" :style="{borderColor: point.color}" >
            <span class="number">{{ convertValuByUnitType(point) }}</span>
            <span class="content" @click="onToggleSeries(point.series)">
              <span>{{ createSeriesName(point.series) }}</span>
            </span>
          </li>
        </ul>
      </el-scrollbar>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import map from 'lodash/map';
import partialRight from 'lodash/partialRight';
import pick from 'lodash/pick';

import { isEmpty } from '@/utils/basic';
import { chartColorSet } from '@/constants/chart-options';
import { plotCharts } from '@/mixins/performance/charts';
import { getDateToString } from '@/utils/date';
import { convertValuByUnit } from '@/utils/number';

const options = {
  chart: {
    type: 'line',
    backgroundColor: 'transparent',
    spacingTop: 5,
    spacingBottom: 0,
    zoomType: 'x',
  },
  title: false,
  credits: false,
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  xAxis: {
    type: 'datetime',
    minRange: 5 * 60 * 1000,
    labels: {
      format: '{value: %Y-%m-%d %H:%M}',
      style: {
        color: '$chinagray',
        fontSize: '9px',
      },
    },
    crosshair: true,
  },
  yAxis: {
    opposite: false,
    minTickInterval: 0,
    startOnTick: false,
  },
  rangeSelector: false,
  scrollbar: false,
  plotOptions: {
    series: {
      animation: false,
      lineWidth: 1.8,
      marker: {
        enabled: true,
        radius: 2,
      },
    },
  },
  series: [],
  colors: chartColorSet,
};

export default {
  name: 'WFlowChart',
  mixins: [plotCharts],
  props: {
    series: Array,
    unitType: String,
  },
  data() {
    return {
      options: cloneDeep(options),
    };
  },
  watch: {
    series(series) {
      this.options.series = series;
    },
  },
  /**
   * 차트가 그려진 후, mouseOver, mouseOut 이벤트를 입력한다.
   * @return {[type]} [description]
   * @author jda
   */
  mounted() {
    const me = this;

    // 차트 높이 조절, load, redraw될 때마다 범례 표현
    Object.assign(this.options.chart, {
      height: 250,
      events: {
        load() {
          me.$nextTick(() => {
            me.setCustomTooltip('init', false);
            me.emitVisibleSeriesInfo();
          });
        },
        redraw() {
          me.$nextTick(() => {
            me.setCustomTooltip('init', false);
          });
        },
      },
    });

    // navigator, drag zoom시 스마트 추적 재로드
    Object.assign(this.options.xAxis, {
      events: {
        setExtremes(e) {
          if (e.triggerOp === 'navigator-drag' && e.DOMEvent.type === 'mousemove') {
            return;
          }

          const startDatetime = isEmpty(e.min) ? e.target.dataMin : e.min;
          const endDatetime = isEmpty(e.max) ? e.target.dataMax : e.max;
          me.$emit('loadSmartTrace', { datetimeParams: { startDateTime: parseInt(startDatetime, 10), endDateTime: parseInt(endDatetime, 10) } });
        },
      },
    });

    // mouseover, mouseout
    Object.assign(this.options.plotOptions.series, {
      point: {
        events: {
          mouseOver() {
            me.setCustomTooltip();
          },
        },
      },
      events: {
        mouseOut() {
          me.setCustomTooltip('init', false);
        },
      },
    });

    // tooltip은 YYYY-MM-DD HH:mm 형식으로 표현
    this.options.tooltip = {
      formatter() {
        return getDateToString(this.x, 'YYYY-MM-DD HH:mm');
      },
    };
  },
  methods: {
    /**
     * 추이 차트를 새로고침한다.
     * @return {[type]} [description]
     * @author jda
     */
    resetChart() {
      const chart = this.$refs.highchart.chart;
      chart.xAxis[0].setExtremes();

      chart.series.forEach((series) => {
        if (!series.visible) {
          series.show();
        }
      });
      this.emitVisibleSeriesInfo();
    },
    /**
     * 추이 차트 범례 이름을 반환한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    createSeriesName(series) {
      if (isEmpty(series)) {
        return null;
      }
      return `${series.userOptions.parentType} 트래픽 사용량`;
    },
    /**
     * unit별로 단위환산한다.
     * @param  {[type]} point [description]
     * @return {[type]}       [description]
     * @author jda
     */
    convertValuByUnitType(point) {
      const unitType = this.unitType;
      let unit = null;
      if (unitType === 'octets') {
        unit = 'Byte';
      } else if (unitType === 'packets') {
        unit = 'count';
      } else {
        unit = unitType;
      }
      return convertValuByUnit(point.y, unit, true, false, true);
    },
    /**
     * 시리즈를 show/hidden 처리
     * @param  {[type]} currentSeries [description]
     * @return {[type]}               [description]
     * @author jda
     */
    onToggleSeries(currentSeries) {
      const visibleSeries = filter(currentSeries.chart.series, 'visible');
      let updateFlag = false;

      if (visibleSeries.length > 1 && currentSeries.visible) {
        updateFlag = true;
        this.legendClass = 'legend-off';
        currentSeries.setVisible(false, false);
      } else if (!currentSeries.visible && visibleSeries.length < 8) {
        updateFlag = true;
        this.legendClass = null;
        currentSeries.setVisible(true, false);
      }

      if (updateFlag) {
        this.emitVisibleSeriesInfo();
      }
    },
    /**
     * 활성화된 시리즈의 info데이터만 emit한다.
     * @return {[type]} [description]
     * @author jda
     */
    emitVisibleSeriesInfo() {
      const visibleSeries = filter(this.legendPoints, 'series.visible');
      const visibleInfoList = map(visibleSeries, partialRight(pick, ['series.userOptions.info', 'series.userOptions.parentType']));

      this.$emit('loadSmartTrace', { visibleInfoList: map(visibleInfoList, 'series.userOptions') });
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/FlowAnalysis/components/chart/FlowChart.vue